import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import useGetDeepgramToken from '@vh/utility/useGetDeepgramToken';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';

/**
 * @typedef {import('./useDeepgram.types').UseDeepgramProps} UseDeepgramProps
 * @typedef {import('./useDeepgram.types').UseDeepgramReturn} UseDeepgramReturn
 * */

// Global connection counter to track all active connections across all hook instances
let globalConnectionCount = 0;

/**
 * React hook that manages microphone streaming & Deepgram live transcription
 * using the official @deepgram/sdk. Designed to replace useDeepgramDirect.
 *
 * NOTE: THIS AUTOCLOSES THE CONNECTION ON TIMEOUTS, SO DOES NOT WORK WITH
 * PUSH TO TALK PTT AS-IS
 * @type {(props: UseDeepgramProps) => UseDeepgramReturn}
 */
export default function useDeepgramWithSDK({ language, paused, onClose }) {
  const { stream, startStream } = useContext(MicrophoneStreamContext) || {};
  const token = useGetDeepgramToken();

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  /** @type {[string|null, Function]} */
  const [error, setError] = useState(null);

  /** @type {[string, Function]} */
  const [currentUnfinishedAffirmation, setCurrentUnfinishedAffirmation] =
    useState('');

  /** @type {[string, Function]} */
  const [currentAffirmation, setCurrentAffirmation] = useState('');

  /** @type {[string, Function]} */
  const [final, setFinal] = useState('');

  /** @type {React.MutableRefObject<any>} */
  const connectionRef = useRef(null);

  /** @type {React.MutableRefObject<MediaRecorder|null>} */
  const recorderRef = useRef(null);

  /** @type {React.MutableRefObject<MediaStream|null>} */
  const audioStreamRef = useRef(null);

  /** @type {React.MutableRefObject<string>} */
  const currentAffirmationRef = useRef('');

  /** @type {React.MutableRefObject<string>} */
  const currentUnfinishedAffirmationRef = useRef('');

  /** @type {React.MutableRefObject<boolean>} */
  const pausedRef = useRef(paused);

  /** @type {React.MutableRefObject<number>} */
  const connectionIdRef = useRef(1); // Start at 1 for cleaner logging

  /** @type {React.MutableRefObject<number>} */
  const cumulativeDurationRef = useRef(0);

  /** @type {React.MutableRefObject<NodeJS.Timeout|null>} */
  const inactivityTimerRef = useRef(null);

  /** @type {React.MutableRefObject<NodeJS.Timeout|null>} */
  const silenceTimeoutRef = useRef(null);

  /** @type {React.MutableRefObject<boolean>} */
  const hasFinalisedRef = useRef(false);

  /** @type {React.MutableRefObject<boolean>} */
  // const ignoreTranscriptsRef = useRef(false); // Flag to ignore transcripts after finalization

  // Add performance tracking
  /** @type {React.MutableRefObject<number>} */
  const connectionStartTimeRef = useRef(0);
  const totalConnectionsRef = useRef(0);

  // Debug: Track hook lifecycle and performance
  useEffect(() => {
    totalConnectionsRef.current += 1;
    console.log(
      `🔊 useDeepgramWithSDK hook mounted (instance #${totalConnectionsRef.current})`
    );

    return () => {
      console.log(
        `🔊 useDeepgramWithSDK hook unmounting (instance #${totalConnectionsRef.current})`
      );
    };
  }, []); // Empty dependency array - only runs on mount/unmount

  // Update refs when state changes
  useEffect(() => {
    currentAffirmationRef.current = currentAffirmation;
  }, [currentAffirmation]);

  useEffect(() => {
    currentUnfinishedAffirmationRef.current = currentUnfinishedAffirmation;
  }, [currentUnfinishedAffirmation]);

  useEffect(() => {
    // Remove frequent logging
    // console.log('🔊 useDeepgramWithSDK: paused state changed from', pausedRef.current, 'to', paused);
    pausedRef.current = paused;
  }, [paused]);

  /** @type {(code?: number, reason?: string, isRemote?: boolean) => void} */
  const internalClose = useCallback(
    (code, reason, isRemote) => {
      const currentConnectionId = connectionIdRef.current;
      console.log(
        `[Connection ${currentConnectionId}] Closing Deepgram connection:`,
        code,
        reason,
        'remote=',
        isRemote
      );

      // Track cleanup performance
      const cleanupStartTime = performance.now();

      // Stop recorder
      if (recorderRef.current) {
        try {
          console.log(
            `🧹 [Connection ${currentConnectionId}] Stopping MediaRecorder...`
          );
          recorderRef.current.stop();
        } catch (e) {
          console.warn(
            `[Connection ${currentConnectionId}] Error stopping MediaRecorder:`,
            e
          );
        }
        recorderRef.current = null;
      }

      // Close connection if it exists
      if (connectionRef.current) {
        try {
          console.log(
            `🧹 [Connection ${currentConnectionId}] Finishing Deepgram connection...`
          );
          console.log(
            `🔌 [Connection ${currentConnectionId}] WebSocket state before close:`,
            connectionRef.current.getReadyState?.() || 'unknown'
          );

          // Force close the WebSocket connection
          connectionRef.current.finish();

          // Decrement global connection count
          globalConnectionCount = Math.max(0, globalConnectionCount - 1);
          console.log(
            `🌐 [Connection ${currentConnectionId}] Closed connection (global count: ${globalConnectionCount})`
          );

          // Set a timeout to force-null the connection reference if it doesn't close cleanly
          setTimeout(() => {
            if (connectionRef.current) {
              console.warn(
                `⚠️ [Connection ${currentConnectionId}] Force-nulling stuck connection reference`
              );
              connectionRef.current = null;
            }
          }, 1000); // 1 second timeout
        } catch (e) {
          console.warn(
            `[Connection ${currentConnectionId}] Error finishing Deepgram connection:`,
            e
          );
        }
        connectionRef.current = null;
      }

      // Clear audio stream reference
      if (audioStreamRef.current) {
        console.log(
          `🧹 [Connection ${currentConnectionId}] Clearing audio stream reference...`
        );
        audioStreamRef.current = null;
      }

      // Clear inactivity timer
      if (inactivityTimerRef.current) {
        console.log(
          `🧹 [Connection ${currentConnectionId}] Clearing inactivity timer...`
        );
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }

      // Clear silence timeout
      if (silenceTimeoutRef.current) {
        console.log(
          `🧹 [Connection ${currentConnectionId}] Clearing silence timeout...`
        );
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }

      // Update state
      setConnected(false);
      setConnecting(false);

      const cleanupTime = performance.now() - cleanupStartTime;
      console.log(
        `⏱️ [Connection ${currentConnectionId}] Cleanup completed in ${cleanupTime.toFixed(
          2
        )}ms`
      );

      // Call onClose callback if this wasn't a remote close
      if (onClose && !isRemote) {
        onClose(code, reason);
      }
    },
    [onClose]
  );

  const resetDeepgramInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      console.warn(
        `[Connection ${connectionIdRef.current}] No transcript received from Deepgram in 10s. Closing connection due to inactivity.`
      );
      setError('Deepgram connection timed out due to inactivity');
      internalClose(408, 'inactivity timeout');
    }, 15000); // 10 seconds
  }, [internalClose]);

  // build locale code from language prop (mirrors previous behaviour)
  const localeCode = useMemo(() => {
    const fallback = 'en-US';
    if (!language) return fallback;
    return language?.deepgramCode || language?.BCP47Code || fallback;
  }, [language]);

  const sdkConfig = useMemo(() => {
    const config = {
      model: 'nova-2',
      language: localeCode,
      punctuate: true,
      interim_results: true,
      filler_words: true,
      smart_format: true,
      endpointing: /** @type {false} */ (false),
      utterance_end_ms: 1250,
      vad_events: false,
    };

    console.log('✅ Final Deepgram SDK config:', config);
    return config;
  }, [localeCode]);

  // Create Deepgram connection
  const connect = useCallback(async () => {
    if (!token) {
      console.log('No token available, skipping connection');
      return;
    }
    // Use the ref to avoid re-creating the callback whenever the `paused` state flips.
    if (pausedRef.current) {
      console.log('Paused, skipping connection');
      return;
    }
    if (connectionRef.current) {
      console.log('Already connected/connecting, skipping');
      return; // already connected/connecting
    }
    if (connecting) {
      console.log('Already in connecting state, skipping');
      return;
    }

    // Track connection start time
    connectionStartTimeRef.current = performance.now();
    const thisConnectionId = connectionIdRef.current;
    console.log(
      `⏱️ [Connection ${thisConnectionId}] Starting connection attempt at ${connectionStartTimeRef.current.toFixed(
        2
      )}ms`
    );
    console.log(
      `🌐 [Connection ${thisConnectionId}] Global connection count before new connection: ${globalConnectionCount}`
    );

    // Reset ignore flag when creating a new connection
    // ignoreTranscriptsRef.current = false;
    cumulativeDurationRef.current = 0; // Reset duration counter

    console.log(
      `[Connection ${thisConnectionId}] Connecting to Deepgram with config:`,
      sdkConfig
    );

    // ensure we have a microphone stream
    let micStream = stream;
    if (!micStream) {
      try {
        const streamStartTime = performance.now();
        micStream = await startStream?.(false);
        const streamDuration = performance.now() - streamStartTime;
        console.log(
          `⏱️ [Connection ${thisConnectionId}] Microphone stream acquired in ${streamDuration.toFixed(
            2
          )}ms`
        );
      } catch (e) {
        console.error(
          `[Connection ${thisConnectionId}] Failed to start microphone stream:`,
          e
        );
        setError(e?.message ?? 'Failed to start microphone');
        return;
      }
    }
    if (!micStream) {
      console.error(`[Connection ${thisConnectionId}] Microphone unavailable`);
      setError('Microphone unavailable');
      return;
    }

    audioStreamRef.current = micStream;
    setConnecting(true);

    // Initialise SDK client & live connection using existing endpoint configuration
    const dgClient = createClient(token);
    const dgConnection = dgClient.listen.live(sdkConfig);

    connectionRef.current = dgConnection;
    hasFinalisedRef.current = false;

    // Track global connections
    globalConnectionCount++;
    console.log(
      `🌐 [Connection ${thisConnectionId}] Created connection (global count: ${globalConnectionCount})`
    );

    dgConnection.on(LiveTranscriptionEvents.Open, () => {
      // Check if this connection is still the current one
      if (thisConnectionId !== connectionIdRef.current) {
        console.log(
          `🔊 [Connection ${thisConnectionId}] Connection opened but is stale (current: ${connectionIdRef.current}), closing...`
        );
        try {
          dgConnection.finish();
        } catch (_) {
          /* noop */
        }
        return;
      }

      // Calculate connection time
      const connectionTime = performance.now() - connectionStartTimeRef.current;
      console.log(
        `⏱️ [Connection ${thisConnectionId}] Connection established in ${connectionTime.toFixed(
          2
        )}ms`
      );

      // Log WebSocket state
      console.log(
        `🔌 [Connection ${thisConnectionId}] WebSocket readyState:`,
        dgConnection.getReadyState?.() || 'unknown'
      );

      console.log('🚀 Final Deepgram config sent:', sdkConfig);
      console.log(
        `🔊 [Connection ${thisConnectionId}] Deepgram connection opened`
      );

      // Start media recorder immediately once socket is open - use 1000ms intervals like the old implementation
      const mediaRecorder = new MediaRecorder(micStream);
      recorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        // Only send data if this is still the current connection and not paused
        if (
          thisConnectionId === connectionIdRef.current &&
          connectionRef.current &&
          event.data.size > 0 &&
          !pausedRef.current
        ) {
          try {
            connectionRef.current.send(event.data);
          } catch (e) {
            console.error(
              `[Connection ${thisConnectionId}] Error sending audio data:`,
              e
            );
          }
        } else if (thisConnectionId !== connectionIdRef.current) {
          console.log(
            `[Connection ${thisConnectionId}] Ignoring audio data - connection is stale (current: ${connectionIdRef.current})`
          );
        }
      };

      mediaRecorder.onstart = () => {
        console.log(
          `[Connection ${thisConnectionId}] MediaRecorder started immediately`
        );
      };

      mediaRecorder.onerror = (event) => {
        console.error(
          `[Connection ${thisConnectionId}] MediaRecorder error:`,
          event
        );
      };

      // Use 1000ms intervals like the old implementation
      mediaRecorder.start(1000);

      // Start inactivity timer to detect network issues
      resetDeepgramInactivityTimer();

      // Add 200ms delay before telling UI we're ready - this gives audio pipeline time to stabilize
      // while still capturing audio immediately
      setTimeout(() => {
        // Double-check connection is still valid before updating UI state
        if (
          thisConnectionId !== connectionIdRef.current ||
          !connectionRef.current
        ) {
          console.log(
            `🔊 [Connection ${thisConnectionId}] Connection no longer valid, skipping UI state update`
          );
          return;
        }

        console.log('set connected true');
        setConnected(true);
        setConnecting(false);
        // setHasEverConnected(true); // Mark that we've successfully connected at least once
        console.log(
          `🔊 [Connection ${thisConnectionId}] UI state updated after 200ms delay - ready for user input`
        );
      }, 200); // 200ms delay before UI shows ready state
    });

    dgConnection.on(LiveTranscriptionEvents.Transcript, (data) => {
      // Reset inactivity timer every time Deepgram sends anything, even silence
      resetDeepgramInactivityTimer();

      // Only process transcripts if this is still the current connection and not paused
      if (thisConnectionId !== connectionIdRef.current || pausedRef.current) {
        if (thisConnectionId !== connectionIdRef.current) {
          console.log(
            `🔊 [Connection ${thisConnectionId}] Ignoring transcript - connection is stale (current: ${connectionIdRef.current})`
          );
        }
        return;
      }

      // // Ignore transcripts if we've finalized and are waiting for connection to close
      // if (ignoreTranscriptsRef.current) {
      //   console.log(
      //     `🔊 [Connection ${thisConnectionId}] Ignoring transcript - post-finalization, waiting for connection close`
      //   );
      //   return;
      // }

      const transcript = data?.channel?.alternatives?.[0]?.transcript ?? '';

      // Debug: Log all transcript data to understand what we're receiving
      console.log(`🔊 [Connection ${thisConnectionId}] Raw transcript data:`, {
        transcript,
        type: data?.type,
        is_final: data?.is_final,
        speech_final: data?.speech_final,
        duration: data?.duration,
        start: data?.start,
        end: data?.end,
      });

      // TIMER-BASED APPROACH - simpler and more reliable than duration-based
      if (transcript) {
        console.log(
          `🔊 [Connection ${thisConnectionId}] Got transcript content: "${transcript}"`
        );

        // Clear any existing silence timeout since we got new content
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }

        if (data?.is_final) {
          // is_final means this segment is complete, but speech may continue
          console.log(
            `🔊 [Connection ${thisConnectionId}] Final segment:`,
            transcript
          );
          const newAffirmation = currentAffirmationRef.current
            ? currentAffirmationRef.current + ' ' + transcript
            : transcript;
          setCurrentAffirmation(newAffirmation);
          setCurrentUnfinishedAffirmation(''); // Clear interim text
        } else {
          // Interim result - update the unfinished part
          setCurrentUnfinishedAffirmation(transcript);
        }

        // Start a 5-second timer for manual timeout fallback (generous since UtteranceEnd should handle most cases)
        silenceTimeoutRef.current = setTimeout(() => {
          if (hasFinalisedRef.current) {
            return;
          }
          const finalText =
            currentAffirmationRef.current ||
            currentUnfinishedAffirmationRef.current;

          console.log('thisConnectionId === connectionIdRef.current');
          console.log(thisConnectionId === connectionIdRef.current);
          if (finalText && thisConnectionId === connectionIdRef.current) {
            console.log(
              `⏲️ Manual timeout triggered after 5s silence, finalizing text:`,
              finalText
            );
            hasFinalisedRef.current = true;
            setFinal(finalText);
            setCurrentAffirmation('');
            setCurrentUnfinishedAffirmation('');
            // ignoreTranscriptsRef.current = true;
            internalClose(1000, 'finalized');
          }
        }, 5000); // 5 seconds - generous fallback timeout
        console.log(
          `🔊 [Connection ${thisConnectionId}] set idle timeout submission:`,
          silenceTimeoutRef.current
        );
      }
    });

    dgConnection.on(LiveTranscriptionEvents.UtteranceEnd, (data) => {
      const thisConnectionId = connectionIdRef.current;
      console.log(
        `🎯 [Connection ${thisConnectionId}] UtteranceEnd event:`,
        data
      );

      let finalText = '';
      const transcript = data?.channel?.alternatives?.[0]?.transcript ?? '';
      if (transcript) {
        finalText = transcript;
        console.log(
          `🔊 [Connection ${thisConnectionId}] Using transcript from UtteranceEnd event:`,
          transcript
        );
      } else {
        // Properly concatenate accumulated final text and current interim text, like currentTranscript does
        console.log(
          `🔊 [Connection ${thisConnectionId}] Concatenating: currentAffirmation="${currentAffirmationRef.current}" + currentUnfinishedAffirmation="${currentUnfinishedAffirmationRef.current}"`
        );
        if (currentAffirmationRef.current) {
          finalText =
            currentAffirmationRef.current +
            (currentAffirmationRef.current.length > 0 &&
            currentUnfinishedAffirmationRef.current.length > 0
              ? ' '
              : '') +
            currentUnfinishedAffirmationRef.current;
        } else if (currentUnfinishedAffirmationRef.current) {
          finalText = currentUnfinishedAffirmationRef.current;
        }
      }

      if (finalText) {
        if (hasFinalisedRef.current) {
          return;
        }
        hasFinalisedRef.current = true;
        setFinal(finalText);
        setCurrentAffirmation('');
        setCurrentUnfinishedAffirmation('');
        // ignoreTranscriptsRef.current = true;
        console.log(
          `🔊 [Connection ${thisConnectionId}] Finalized on UtteranceEnd:`,
          finalText
        );
        internalClose(1000, 'finalized');
      } else {
        console.log(
          `🔊 [Connection ${thisConnectionId}] UtteranceEnd: No transcript to finalize`
        );
      }
    });

    dgConnection.on(LiveTranscriptionEvents.Error, (err) => {
      // Only handle errors for the current connection
      if (thisConnectionId !== connectionIdRef.current) {
        console.log(
          `🔊 [Connection ${thisConnectionId}] Ignoring error - connection is stale (current: ${connectionIdRef.current}):`,
          err
        );
        return;
      }

      console.error(`🔊 [Connection ${thisConnectionId}] Deepgram error:`, err);
      setError(err?.message || 'Deepgram error');
      internalClose(4000, 'Deepgram error');
    });

    dgConnection.on(LiveTranscriptionEvents.Close, (event) => {
      // Only handle close events for the current connection
      if (thisConnectionId !== connectionIdRef.current) {
        console.log(
          `🔊 [Connection ${thisConnectionId}] Ignoring close event - connection is stale (current: ${connectionIdRef.current}):`,
          event
        );
        return;
      }

      console.log(
        `🔊 [Connection ${thisConnectionId}] Deepgram connection closed:`,
        event
      );
      console.log(
        `🔌 [Connection ${thisConnectionId}] WebSocket close - code: ${event?.code}, reason: "${event?.reason}", wasClean: ${event?.wasClean}`
      );
      internalClose(event?.code, event?.reason, true);
    });
  }, [
    token,
    connecting,
    sdkConfig,
    stream,
    startStream,
    resetDeepgramInactivityTimer,
    internalClose,
  ]);

  // manage connection on pause/resume changes
  useEffect(() => {
    // Remove frequent verbose logging
    // const timestamp = performance.now().toFixed(2);
    // console.log(`🔊 useDeepgramWithSDK: pause/resume effect triggered at ${timestamp}ms with paused =`, paused, 'token =', !!token);

    // Remove verbose state logging
    // console.log('🔍 Current state:', {
    //   paused,
    //   hasToken: !!token,
    //   connected,
    //   connecting,
    //   currentConnectionId: connectionIdRef.current,
    //   hasConnection: !!connectionRef.current,
    //   hasRecorder: !!recorderRef.current
    // });

    if (paused) {
      console.log(
        `🔊 [Connection ${connectionIdRef.current}] Pausing - closing current connection`
      );
      // Reset transcript state when pausing, like the old implementation
      setCurrentAffirmation('');
      setCurrentUnfinishedAffirmation('');
      setFinal('');
      internalClose(1000, 'paused');
    } else if (token && !connectionRef.current && !connecting) {
      // Only increment connection ID and connect when we actually need a new connection
      // Add guards to prevent multiple simultaneous connection attempts
      connectionIdRef.current += 1;
      console.log(
        `🔊 [Connection ${connectionIdRef.current}] Resuming - starting new connection`
      );

      // Add a small delay to ensure cleanup is complete before starting new connection
      const connectTimeout = setTimeout(() => {
        // Double-check we still need a connection before actually connecting
        if (!connectionRef.current && !pausedRef.current && token) {
          console.log(
            `🔊 [Connection ${connectionIdRef.current}] Delayed connect executing`
          );
          connect();
        } else {
          // Remove verbose logging here too
          console.log(
            `🔊 [Connection ${connectionIdRef.current}] Skipping delayed connect - conditions changed`
          );
        }
      }, 10); // Keep at 10ms to avoid adding more performance overhead

      return () => {
        clearTimeout(connectTimeout);
      };
    } else {
      // Remove this frequent logging
      // console.log('🔊 useDeepgramWithSDK: Not connecting because:', {
      //   hasToken: !!token,
      //   hasConnection: !!connectionRef.current,
      //   isConnecting: connecting,
      //   reason: !token ? 'no token' : connectionRef.current ? 'already has connection' : connecting ? 'already connecting' : 'unknown'
      // });
    }
  }, [paused, token, connect, internalClose, connecting]); // Removed connected and connecting from dependencies

  // cleanup on unmount
  useEffect(() => {
    return () => {
      internalClose(1000, 'component unmount');
    };
  }, [internalClose]);

  const resetTranscript = useCallback(() => {
    setCurrentUnfinishedAffirmation('');
    setFinal('');
    setCurrentAffirmation('');
  }, []);

  // Compute current transcript like the old implementation
  const currentTranscript = useMemo(() => {
    if (currentAffirmation) {
      return (
        currentAffirmation +
        (currentAffirmation.length > 0 ? ' ' : '') +
        currentUnfinishedAffirmation
      );
    } else if (currentUnfinishedAffirmation) {
      return currentUnfinishedAffirmation;
    }
    return '';
  }, [currentAffirmation, currentUnfinishedAffirmation]);

  return {
    // hasAttemptedConnection,
    reconnect: connect,
    connected,
    connecting,
    error,
    currentAffirmation,
    currentUnfinishedAffirmation,
    currentTranscript,
    finalAffirmation: final,
    resetTranscript,
    closeSocket: internalClose,
  };
}
